import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSocket } from 'src/context/SocketContext';

const columns = [
  { id: 'user_id', label: 'User ID', align: 'start' },
  { id: 'plan_name', label: 'Plan Name', align: 'center' },
  { id: 'purchase_date', label: 'Purchase Date', align: 'center' },
  { id: 'price', label: 'Price', align: 'center' },
  { id: 'payment_status', label: 'Payment Status', align: 'center' },
  { id: 'product_name', label: 'Product Name', align: 'center' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date)
    ? '-'
    : date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
};

const priceChange = (price) => {
  if (!price || isNaN(price)) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

const UserPlanTable = ({ status }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [totalPrice, setTotalPrice] = useState(0);
  const [clearFilter, setClearFilter] = useState(false);
  const socket = useSocket();

  const fetchWebinarRooms = async (page, limit, startDate, endDate) => {
    setLoading(true);
    try {
      const skip = page * limit;
      const params = { skip, limit };
      if (status) params.payment_status = status;
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      socket.emit('userPlaneList', params, (data) => {
        const { result = [], summary } = data;
        const formattedRows = result?.result?.map((doc) => ({
          id: doc?._id,
          user_id: doc.user_id || '-',
          plan_name: doc.plan_name || '-',
          purchase_date: formatDate(doc.purchase_date),
          price: doc.price ? priceChange(doc.price) : '-',
          payment_status: doc.payment_status || '-',
          product_name: doc.product_name || '-',
        }));
        console.log('🚀 ~ socket.emit ~ summary:', summary);

        setTotalPrice(summary);
        setRows(formattedRows);
        setTotalCount(result.total);
      });
    } catch (error) {
      console.error('Error fetching webinar rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebinarRooms(page, rowsPerPage, dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilter = () => {
    setClearFilter(true);
    fetchWebinarRooms(0, rowsPerPage, dateRange.startDate, dateRange.endDate);
    setPage(0);
  };

  const handleClearFilter = () => {
    setClearFilter(false);
    setDateRange({ startDate: null, endDate: null });
    fetchWebinarRooms(0, rowsPerPage, null, null);
    setPage(0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Filter Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            gap: 2,
            flexWrap: 'wrap',
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, startDate: newValue }))}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 140, '.MuiInputBase-input': { p: 1 } },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, endDate: newValue }))}
              minDate={dateRange.startDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 140, '.MuiInputBase-input': { p: 1 } },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              disabled={!dateRange.startDate && !dateRange.endDate}
            >
              Apply Filter
            </Button>
            {clearFilter && (
              <Button variant="outlined" color="secondary" onClick={handleClearFilter}>
                Clear Filter
              </Button>
            )}
          </Box>
          <Box sx={{ fontWeight: 'bold', ml: '700px' }}>
            {status === 'completed' ? (
              <>
                <span style={{ color: '#7a8994' }}>Payment Received: </span>
                <span style={{ color: 'green' }}>{priceChange(totalPrice)}</span>
              </>
            ) : (
              <>
                <span style={{ color: '#7a8994' }}>Pending Payments: </span>
                <span style={{ color: 'orange' }}>{priceChange(totalPrice)}</span>
              </>
            )}
          </Box>
          <Box sx={{ fontWeight: 'bold', color: '#7a8994' }}>Total: {totalCount}</Box>
        </Box>

        {/* Table Section */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 260px)',
                backgroundColor: '#fff',
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold' }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        No data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} sx={{ height: 36 }}>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              padding: '4px 12px',
                              lineHeight: 1.2,
                              fontSize: '0.85rem',
                            }}
                          >
                            {row[column.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                p: 1,
                // borderTop: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                // height: '64px',
              }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default UserPlanTable;
